import { useState } from 'react'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { OrderErrorCode } from 'lib/graphql/storefront'
import { handleApolloError } from 'config/helpers'
import { useStoreInformationQuery } from 'lib/graphql/storefront'
import {
  DEFAULT_PERIOD_ORDER_CREATE,
  DEFAULT_PERIOD_ORDER_UPDATE,
  DEFAULT_PERIOD_ORDER_CANCEL,
} from 'config'

const useOrderResultHandling = () => {
  const { t } = useTranslation()
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [alertStatus, setAlertStatus] = useState<
    'success' | 'danger' | 'warning'
  >('danger')

  const { data: StoreInformationData } = useStoreInformationQuery()

  const errorPosition = (field: Array<string>) => {
    if (field.length > 0) {
      const positions = []
      field?.forEach((item: string) => {
        switch (item) {
          case 'start':
            positions.push(
              `「${t('share.booking-info-placeholder-post-date')}」`
            )
            break
          case 'end':
            positions.push(
              `「${t('share.booking-info-placeholder-end-date')}」`
            )
            break
          case 'numberOfPeople':
            positions.push(
              `「${t('share.booking-info-title-purpose-number')}」`
            )
            break
          case 'contactOfRepresentative':
            positions.push(
              `「${t('share.booking-info-title-purpose-number')}」`
            )
            break
          case 'purpose':
            positions.push(`「${t('share.booking-info-title-purpose')}」`)
            break
          case 'saleOfGoods':
            positions.push(`「${t('share.booking-info-title-saleOfGoods')}」`)
            break
          case 'entryFee':
            positions.push(`「${t('share.booking-info-title-fee-collect')}」`)
            break
          case 'calendar':
            positions.push(`「${t('share.booking-confirm-schedule')}」`)
            break
          case 'approval':
            positions.push(
              `「${t('share.booking-info-title-usage-schedule')}」`
            )
            break
          case 'organizer':
            positions.push(
              `「${t('share.booking-info-title-organizer-name')}」`
            )
            break
          case 'eventName':
            positions.push(
              `「${t('share.booking-info-title-publication-name')}」`
            )
            break
          case 'publishableDate':
            positions.push(`「${t('share.booking-info-title-post-date')}」`)
            break
          case 'id':
          case 'variantId':
            positions.push(`「${t('share.booking-info-variants')}」`)
            break
          case 'quantity':
            positions.push(`「${t('share.booking-info-variants-quantity')}」`)
            break
        }
      })
      return (
        t('errors.error-field-position.front') +
        positions?.filter((item) => item) +
        t('errors.error-field-position.back')
      )
    } else return ''
  }

  const errorHandling = (error, type: 'create' | 'update' | 'cancel') => {
    setAlertStatus('danger')
    if (Array.isArray(error) && error.length > 0) {
      error?.forEach((value) => {
        switch (value?.code) {
          case OrderErrorCode.OverMaxConsecutiveDays:
            setAlertMessage(
              t(`errors.order-create-update-error-${value.code}`) +
                t('share.facility-detail-note-1.front') +
                StoreInformationData?.storeInformation?.maxConsecutiveDays +
                t('share.facility-detail-note-1.middle') +
                `${
                  StoreInformationData?.storeInformation?.maxConsecutiveDays + 1
                }` +
                t('share.facility-detail-note-1.back')
            )
            break
          case OrderErrorCode.TooClose:
            let m = null
            let d = null
            let h = null
            if (type === 'create') {
              m = DEFAULT_PERIOD_ORDER_CREATE['months']
              d = DEFAULT_PERIOD_ORDER_CREATE['days']
              h = DEFAULT_PERIOD_ORDER_CREATE['hours']
            }
            if (type === 'update') {
              m = DEFAULT_PERIOD_ORDER_UPDATE['months']
              d = DEFAULT_PERIOD_ORDER_UPDATE['days']
              h = DEFAULT_PERIOD_ORDER_UPDATE['hours']
            }
            if (type === 'cancel') {
              m = DEFAULT_PERIOD_ORDER_CANCEL['months']
              d = DEFAULT_PERIOD_ORDER_CANCEL['days']
              h = DEFAULT_PERIOD_ORDER_CANCEL['hours']
            }
            const str =
              `${m ? m + t('share.period-before.months') : ''}` +
              `${d ? d + t('share.period-before.days') : ''}` +
              `${
                h
                  ? (m | d && t('share.before-of')) +
                    (h + t('share.period-before.hour'))
                  : t('share.before')
              }`
            setAlertMessage(
              t(`errors.order-${type}-error-TOO_CLOSE.front`) +
                str +
                t(`errors.order-${type}-error-TOO_CLOSE.back`)
            )
            break
          case OrderErrorCode.Blank:
          case OrderErrorCode.Invalid:
          case OrderErrorCode.NotFound:
          case OrderErrorCode.Unavailable:
          case OrderErrorCode.UndefinedProduct:
          case OrderErrorCode.CustomerNotFound:
          case OrderErrorCode.NotDraft:
          case OrderErrorCode.NotForSale:
          case OrderErrorCode.Duplicate:
          case OrderErrorCode.QuantityZero:
          case OrderErrorCode.TooShort:
          case OrderErrorCode.TooLong:
          case OrderErrorCode.InvalidDate:
          case OrderErrorCode.UnavailableProduct:
          case OrderErrorCode.Required:
          case OrderErrorCode.PastOrder:
          case OrderErrorCode.ShouldBeUpdated:
          case OrderErrorCode.DuplicateDates:
          case OrderErrorCode.InvalidPeriod:
          case OrderErrorCode.DateNotFound:
          case OrderErrorCode.InvalidCheckinTime:
          case OrderErrorCode.InvalidCheckoutTime:
          case OrderErrorCode.CheckinRequired:
          case OrderErrorCode.TimestampDates:
          case OrderErrorCode.FutureDate:
          case OrderErrorCode.Disapproved:
          case OrderErrorCode.Canceled:
          case OrderErrorCode.RateAndPeriodMissing:
          case OrderErrorCode.RateAndPeriodGiven:
          case OrderErrorCode.ApprovalRequired:
          case OrderErrorCode.OverlappingOrders:
          case OrderErrorCode.OutsidePeriodForSale:
          case OrderErrorCode.ResaervationSlotConflict:
            setAlertMessage(
              t(`errors.order-create-update-error-${value.code}`) +
                errorPosition(value?.field)
            )
            break
          default:
            setAlertMessage(t('errors.order-create-update-error-DEFAULT'))
        }
      })
    } else {
      setAlertMessage(t(handleApolloError(error)))
    }
  }

  // TODO: 必要に応じて適宜追加
  const successHandling = (type: 'update' | 'cancel') => {
    switch (type) {
      case 'cancel':
        setAlertMessage(t('share.order-cancel-successful'))
        break
      case 'update':
      default:
        setAlertMessage(t('share.order-information-updated'))
        break
    }
    setAlertStatus('success')
  }

  const warningHandling = (type: 'update' | 'cancel') => {
    // TODO: 確認メール以外の warning があれば追加
    setAlertStatus('warning')
    setAlertMessage(
      `${t(
        `errors.order-create-update-warning-MAIL_SENDING_ERROR.${type}`
      )} ${t('errors.order-create-update-warning-MAIL_SENDING_ERROR.end')}`
    )
  }

  return {
    alertMessage,
    alertStatus,
    setAlertMessage,
    setAlertStatus,
    errorHandling,
    successHandling,
    warningHandling,
  }
}

export default useOrderResultHandling
